import React from 'react'
import { Avatar, Button, MessageEditor, RightDrawer } from '@/shared/ui'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { useCommunityDraftAttachments } from '../hooks/useCommunityDraftAttachments'
import type {
  CommunityMessageAttachment,
  CommunityThread,
} from '../services/communityService'
import { COMMUNITY_ATTACHMENT_ACCEPT } from '../utils/communityAttachments'
import {
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '../utils/communityMessageContent'
import { CommunityCardReactionsRow } from './CommunityCardReactionsRow'
import { CommunityDraftAttachmentsPreview } from './CommunityDraftAttachmentsPreview'
import { CommunityEmojiIcon } from './CommunityEmojiIcon'
import { EmojiPicker } from './EmojiPicker'
import { CommunityLinkPreviewCard } from './CommunityLinkPreviewCard'
import { CommunityMessageAttachments } from './CommunityMessageAttachments'
import { CommunityMessageText } from './CommunityMessageText'
import { SmilePlus } from 'lucide-react'
import { computeEmojiPickerPlacement } from '@/shared/ui/emojiPickerPlacement'
import { extractMentionUserIdsFromHtml } from '@/shared/utils/mentions'
import {
  buildLinkPreview,
  extractFirstUrlFromHtml,
  type CommunityLinkPreview,
} from '../services/linkPreviewService'
import { useCommunityMentionUsers } from '../hooks/useCommunityMentionUsers'
import { useCommunityMessageFocus } from '../hooks/useCommunityMessageFocus'

export type CommunityDrawerThreadHook = {
  thread: CommunityThread
  loading: boolean
  loadedOnce?: boolean
  sending: boolean
  sendMessage: (content: string, attachments?: File[]) => Promise<void> | void
  toggleCardEmoji: (emoji: string) => Promise<void> | void
  toggleMessageEmoji: (messageId: number, emoji: string) => Promise<void> | void
  deleteMessage?: (messageId: number) => Promise<void> | void
  editMessage?: (messageId: number, content: string) => Promise<void> | void
}

type CommunityDrawerBaseProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  header: React.ReactNode
  headerRight?: React.ReactNode
  threadHook: CommunityDrawerThreadHook
  currentUserId: string | null
  isMaster: boolean
  isModerator?: boolean
  footerHint: string
  focusMessageId?: number | string | null
  overlayContent?: React.ReactNode
}

export function CommunityDrawerBase({
  open,
  onOpenChange,
  header,
  headerRight,
  threadHook,
  currentUserId,
  isMaster,
  isModerator = false,
  footerHint,
  focusMessageId,
  overlayContent,
}: CommunityDrawerBaseProps) {
  const isLg =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 1024px)').matches

  const {
    thread,
    loading,
    loadedOnce,
    sending,
    sendMessage,
    toggleCardEmoji,
    toggleMessageEmoji,
    deleteMessage,
    editMessage,
  } = threadHook

  const mentionUserIds = React.useMemo(() => {
    const ids = new Set<string>()
    thread.messages.forEach((msg) => {
      const html = String(msg.content || '')
      extractMentionUserIdsFromHtml(html).forEach((id) => ids.add(id))
    })
    return Array.from(ids)
  }, [thread.messages])

  const mentionUsersById = useCommunityMentionUsers(mentionUserIds)

  const [editorValue, setEditorValue] = React.useState('')
  const [linkPreview, setLinkPreview] = React.useState<CommunityLinkPreview | null>(null)
  const [editingMessageId, setEditingMessageId] = React.useState<number | null>(null)
  const [openEmojiForMessageId, setOpenEmojiForMessageId] = React.useState<number | null>(null)
  const [emojiPickerPlacement, setEmojiPickerPlacement] =
    React.useState<'top' | 'bottom'>('bottom')
  const [emojiPickerAlign, setEmojiPickerAlign] = React.useState<'left' | 'right'>('left')
  const { formatUserDateTime } = useUserDateFormatter()
  const messagesContainerRef = React.useRef<HTMLDivElement | null>(null)
  const { highlightedMessageId, disableAutoScrollToBottom } =
    useCommunityMessageFocus({
      open,
      focusMessageId,
      messages: thread.messages,
      containerRef: messagesContainerRef as any,
    })
  const {
    attachments: draftAttachments,
    addFiles: addDraftFiles,
    removeAttachment,
    clear: clearDraftAttachments,
    error: attachmentError,
  } = useCommunityDraftAttachments()
  const [lightboxOpen, setLightboxOpen] = React.useState(false)
  const [lightboxAttachments, setLightboxAttachments] = React.useState<
    CommunityMessageAttachment[]
  >([])
  const [lightboxIndex, setLightboxIndex] = React.useState(0)

  const handleOpenLightbox = React.useCallback(
    (attachments: CommunityMessageAttachment[], index: number) => {
      if (!attachments || !attachments.length) return
      const normalized = attachments.filter((att) => att && att.url)
      if (!normalized.length) return
      const safeIndex = Math.max(0, Math.min(index, normalized.length - 1))
      setLightboxAttachments(normalized)
      setLightboxIndex(safeIndex)
      setLightboxOpen(true)
    },
    [],
  )

  const handleFilesAdded = React.useCallback(
    (files: File[]) => {
      addDraftFiles(files)
    },
    [addDraftFiles],
  )

  const handleSend = React.useCallback(() => {
    const html = String(editorValue || '')
    const prepared = prepareCommunityMessageContent(html)
    const filesToSend = draftAttachments.map((item) => item.file)
    if ((!prepared.text && !filesToSend.length) || sending) return

    // limpa editor imediatamente para o usuário
    setEditorValue('')
    setLinkPreview(null)
    clearDraftAttachments()

    // edição de mensagem existente
    if (editingMessageId != null && typeof editMessage === 'function') {
      void editMessage(editingMessageId, html)
      setEditingMessageId(null)
      return
    }

    // nova mensagem com anexos
    void sendMessage(html, filesToSend)
  }, [
    clearDraftAttachments,
    draftAttachments,
    editMessage,
    editingMessageId,
    editorValue,
    sendMessage,
    sending,
  ])

  React.useEffect(() => {
    if (!open) {
      clearDraftAttachments()
      setLightboxOpen(false)
      setLightboxAttachments([])
      setLightboxIndex(0)
      setEditingMessageId(null)
      setOpenEmojiForMessageId(null)
    }
  }, [clearDraftAttachments, open])

  React.useEffect(() => {
    if (!open) return
    if (disableAutoScrollToBottom) return
    const container = messagesContainerRef.current
    if (!container) return

    const scrollToBottom = () => {
      try {
        container.scrollTop = container.scrollHeight
      } catch {
        // ignora falhas transitórias de layout
      }
    }

    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      const handle = window.requestAnimationFrame(scrollToBottom)
      return () => window.cancelAnimationFrame(handle)
    }

    scrollToBottom()
  }, [disableAutoScrollToBottom, open, thread.messages.length])

  React.useEffect(() => {
    if (!open) {
      setLinkPreview(null)
      return
    }
    const url = extractFirstUrlFromHtml(editorValue || '')
    if (!url) {
      setLinkPreview(null)
      return
    }
    const preview = buildLinkPreview(url)
    setLinkPreview(preview)
  }, [editorValue, open])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    handleSend()
  }

  const hasLightbox = lightboxOpen && lightboxAttachments.length > 0
  const lightboxTotal = lightboxAttachments.length
  const lightboxCurrentIndex =
    hasLightbox && lightboxTotal > 0
      ? Math.max(0, Math.min(lightboxIndex, lightboxTotal - 1))
      : 0
  const lightboxCurrent =
    hasLightbox && lightboxTotal > 0 ? lightboxAttachments[lightboxCurrentIndex] : null

  const content = (
    <div className="relative flex h-full flex-col gap-3">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[#1a1a1a]" />

      <div
        ref={messagesContainerRef}
        className="mt-1 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-1"
      >
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground/70">
          <span>Reações do card:</span>
          <CommunityCardReactionsRow
            reactions={thread.cardReactions}
            onToggleEmoji={toggleCardEmoji}
          />
        </div>

        {loading && !loadedOnce && thread.messages.length === 0 && (
          <div className="text-xs text-foreground/70">Carregando conversas...</div>
        )}
        {loadedOnce && thread.messages.length === 0 && (
          <div className="text-xs text-foreground/70">
            Nenhuma mensagem por enquanto. Seja o primeiro a comentar.
          </div>
        )}
        {thread.messages.map((msg) => {
          const isAuthor =
            currentUserId && String(msg.authorId) === String(currentUserId)
          const canDelete = !!deleteMessage && (isMaster || isModerator || !!isAuthor)
          const canEdit = !!editMessage && !!isAuthor
          const isPickerOpen = openEmojiForMessageId === msg.id
          const isHighlighted = Number(highlightedMessageId) === Number(msg.id)

          return (
            <div
              key={msg.id}
              data-community-message-id={msg.id}
              className={[
                'group flex items-start gap-2 rounded-md px-2 py-2 transition-colors duration-150 hover:bg-[#202020]',
                isHighlighted ? 'ring-2 ring-sky-500/60 bg-sky-500/5 hover:bg-sky-500/10' : '',
              ].join(' ')}
            >
              <Avatar
                src={msg.authorAvatarUrl || undefined}
                alt={msg.authorName}
                className="h-7 w-7"
                fallback={msg.authorName?.[0] || 'U'}
              />
              <div className="flex-1">
                <div className="flex items-baseline gap-2 text-[11px]">
                  <span className="font-semibold text-foreground">
                    {msg.authorName || 'Usuário'}
                    {isAuthor && (
                      <span className="ml-1 text-[10px] text-foreground/60">
                        (você)
                      </span>
                    )}
                  </span>
                  {msg.createdAt && (
                    <span className="text-[#454545]">
                      {formatUserDateTime(msg.createdAt)}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[13px] text-foreground/90 font-normal">
                  {(() => {
                    const raw = String(msg.content || '')
                    const { html: base, isEdited } = stripEditedSuffixFromHtml(raw)
                    return (
                      <>
                        <CommunityMessageText
                          text={base}
                          mentionUsersById={mentionUsersById}
                        />
                        {isEdited && (
                          <span className="whitespace-pre-line italic font-thin text-[#454545]">
                            {' '}
                            (editado)
                          </span>
                        )}
                      </>
                    )
                  })()}
                </div>
                <CommunityMessageAttachments
                  attachments={msg.attachments}
                  onOpenLightbox={handleOpenLightbox}
                />
                {(() => {
                  const url = extractFirstUrlFromHtml(String(msg.content || ''))
                  const preview = url ? buildLinkPreview(url) : null
                  if (!preview) return null
                  return (
                    <div className="mt-2">
                      <CommunityLinkPreviewCard preview={preview} />
                    </div>
                  )
                })()}
                <div className="mt-1 flex flex-col gap-1 text-[12px]">
                  <div className="flex flex-wrap items-center gap-2">
                    {msg.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={() => toggleMessageEmoji(msg.id, r.emoji)}
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                          r.reactedByCurrentUser
                            ? 'border-emerald-400 bg-emerald-500/10'
                            : 'border-transparent bg-[#454545]',
                        ].join(' ')}
                      >
                        <CommunityEmojiIcon emoji={r.emoji} loading="eager" showFallback />
                        <span>{r.count}</span>
                      </button>
                    ))}
                    <div className="relative inline-flex">
                      <button
                        type="button"
                        onClick={(event) => {
                          const alreadyOpen = openEmojiForMessageId === msg.id
                          if (alreadyOpen) {
                            setOpenEmojiForMessageId(null)
                            return
                          }

                          if (!isLg) {
                            setOpenEmojiForMessageId(msg.id)
                            return
                          }

                          const containerEl = messagesContainerRef.current
                          const buttonRect = event.currentTarget.getBoundingClientRect()

                          const containerRect = containerEl
                            ? containerEl.getBoundingClientRect()
                            : null
                          const { placement, align } = computeEmojiPickerPlacement({
                            buttonRect,
                            containerRect,
                            estimatedPickerHeight: 520,
                            pickerWidth: 320,
                          })

                          setEmojiPickerPlacement(placement)
                          setEmojiPickerAlign(align)
                          setOpenEmojiForMessageId(msg.id)
                        }}
                        className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f1f1f] text-[#b3b3b3] hover:bg-[#262626] hover:text-[#e5e5e5]"
                      >
                        <SmilePlus className="h-4 w-4" />
                      </button>
                      {isPickerOpen && isLg && (
                        <div
                          className={[
                            'absolute z-[90]',
                            emojiPickerPlacement === 'top'
                              ? 'bottom-[calc(100%+0.5rem)]'
                              : 'top-7',
                            emojiPickerAlign === 'right' ? 'right-0' : 'left-0',
                          ].join(' ')}
                        >
                          <EmojiPicker
                            canDeleteCustomEmojis={isMaster}
                            onSelect={async (emoji) => {
                              await toggleMessageEmoji(msg.id, emoji)
                              setOpenEmojiForMessageId(null)
                            }}
                            onClose={() => setOpenEmojiForMessageId(null)}
                          />
                        </div>
                      )}
                    </div>
                    {!isLg && (
                      <BottomDrawer
                        open={isPickerOpen}
                        onOpenChange={(next) => {
                          setOpenEmojiForMessageId(next ? msg.id : null)
                        }}
                        header="Emojis"
                        className="h-[50vh] max-h-[50vh] bg-[#111111]"
                        zIndexClass="z-[180000]"
                        overlayClassName="bg-black/60 backdrop-blur-sm"
                      >
                        <EmojiPicker
                          className="w-full"
                          canDeleteCustomEmojis={isMaster}
                          onSelect={async (emoji) => {
                            await toggleMessageEmoji(msg.id, emoji)
                            setOpenEmojiForMessageId(null)
                          }}
                          onClose={() => setOpenEmojiForMessageId(null)}
                        />
                      </BottomDrawer>
                    )}
                    {(canEdit || canDelete) && (
                      <div className="ml-3 flex items-center gap-2 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => {
                              const raw = String(msg.content || '')
                              const base = stripEditedSuffixFromHtml(raw).html
                              setEditorValue(base)
                              clearDraftAttachments()
                              setEditingMessageId(msg.id)
                            }}
                            className="font-medium text-sky-400 hover:underline"
                          >
                            Editar
                          </button>
                        )}
                        {canDelete && deleteMessage && (
                          <button
                            type="button"
                            onClick={() => deleteMessage(msg.id)}
                            className="font-medium text-red-400 hover:text-red-300"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {hasLightbox && lightboxCurrent && (
        <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/80 px-4 py-6">
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white hover:bg-black/80"
          >
            Fechar
          </button>

          {lightboxTotal > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || lightboxTotal <= 1) return
                setLightboxIndex((prev) =>
                  prev <= 0 ? lightboxTotal - 1 : prev - 1,
                )
              }}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              ‹
            </button>
          )}

          <div className="max-h-[80vh] max-w-[80vw]">
            <img
              src={lightboxCurrent.url}
              alt="Imagem da mensagem da comunidade"
              className="h-full w-full rounded-lg object-contain shadow-2xl"
            />
          </div>

          {lightboxTotal > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || lightboxTotal <= 1) return
                setLightboxIndex((prev) =>
                  prev >= lightboxTotal - 1 ? 0 : prev + 1,
                )
              }}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              ›
            </button>
          )}

          {lightboxTotal > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
              {lightboxCurrentIndex + 1} / {lightboxTotal}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 h-px bg-[#161616]" />
      <form onSubmit={handleSubmit} className="mt-2 space-y-2 pt-2">
        {attachmentError && (
          <div className="text-[11px] text-red-400">
            {attachmentError}
          </div>
        )}
        {linkPreview && (
          <div className="mb-2">
            <CommunityLinkPreviewCard preview={linkPreview} />
          </div>
        )}
        <CommunityDraftAttachmentsPreview
          attachments={draftAttachments}
          onRemove={removeAttachment}
        />
        <MessageEditor
          variant="slack"
          enableMentions
          value={editorValue}
          onChange={setEditorValue}
          placeholder="Adicionar uma resposta"
          sendOnEnter
          onSubmit={handleSend}
          submitDisabled={
            sending ||
            (!prepareCommunityMessageContent(editorValue || '').text &&
              !draftAttachments.length)
          }
          toolbar={[]}
          onPasteFiles={handleFilesAdded}
          onPickFiles={handleFilesAdded}
          pickFilesAccept={COMMUNITY_ATTACHMENT_ACCEPT}
          pickFilesMultiple
          stripImages
          renderEmojiPicker={({ onSelect, onClose, className }) => (
            <EmojiPicker
              className={className}
              onSelect={onSelect}
              onClose={onClose}
              canDeleteCustomEmojis={isMaster}
            />
          )}
        />
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-foreground/60">
            {footerHint}
          </div>
          <Button
            type="submit"
            disabled={
              sending ||
              (!prepareCommunityMessageContent(editorValue || '').text &&
                !draftAttachments.length)
            }
            className="h-8 px-3 text-xs hidden"
          >
            Enviar
          </Button>
        </div>
      </form>
    </div>
  )

  const overlaySlot =
    isLg && overlayContent ? (
      <div className="pointer-events-none absolute inset-0">
        <div className="pointer-events-auto flex h-full w-full items-start justify-center px-6 py-6 pr-[30vw]">
          <div
            className="max-h-[calc(100vh-3rem)] w-full max-w-3xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            {overlayContent}
          </div>
        </div>
      </div>
    ) : null

  return isLg ? (
    <RightDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      headerRight={headerRight}
      className="w-[30vw] max-w-none bg-[#1a1a1a]"
      overlayClassName="bg-black/40 backdrop-blur-md"
      variant="high"
      overlayContent={overlaySlot}
    >
      {content}
    </RightDrawer>
  ) : (
    <BottomDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      headerRight={headerRight}
      className="h-[100dvh] max-h-[100vh] bg-[#1a1a1a]"
      zIndexClass="z-[100000]"
      overlayClassName="bg-black/40 backdrop-blur-md"
    >
      {content}
    </BottomDrawer>
  )
}
